import type {PermissionKey, PermissionValue} from "@hosttools/core/models/user";
import {AppContext, useHttpInterceptor, usePrevious, UserContext} from "@hosttools/frontend";
import {ModalConfirm} from "@hosttools/frontend/components";
import {memo, useCallback, useContext, useEffect, useRef} from "react";
import type {ComponentType, FC} from "react";
import {Redirect, Route} from "react-router-dom";
import type {RouteComponentProps, RouteProps} from "react-router-dom";

import Footer from "./components/Footer";
import OnboardingHeader from "./components/OnboardingHeader";
import useSearchQuery from "./hooks/useSearchQuery";

import Errors from "@/admin/components/Errors/Errors";
import Header from "@/admin/components/Header";
import Loader from "@/admin/components/Loader";
import ExternalServices from "@/client/components/ExternalServices";

interface Props extends RouteProps {
    permission?: [PermissionKey, PermissionValue];
    component: ComponentType<RouteComponentProps>;
}

const PrivateRoute: FC<Props> = ({component: Component, permission, ...rest}) => {
    const {confirmModal} = useContext(AppContext);
    const query = useSearchQuery();
    const {isAuthenticated, isLoading, permissions, isLoadingAccounts, user} =
        useContext(UserContext);

    // register the auto login redirection and auto toast message
    useHttpInterceptor();

    // keep track if user has just loaded the page
    const isJustAuthenticatedRef = useRef<boolean>(undefined);
    const lastAuthenticated = usePrevious(isAuthenticated);
    useEffect(() => {
        if (!lastAuthenticated && isAuthenticated) {
            isJustAuthenticatedRef.current = true;
        } else {
            isJustAuthenticatedRef.current = false;
        }
    }, [isAuthenticated, lastAuthenticated]);

    const handleRender = useCallback(
        (props: RouteComponentProps) => {
            if (isAuthenticated === true && !isLoading && !isLoadingAccounts) {
                const isDemo = user.isDemo && user.subscriptionStatus !== "active";
                const isOnboarding = query.get("onboarding");

                const mainElem = (
                    <>
                        {!isOnboarding && <Header />}
                        {!isOnboarding && <Errors />}
                        {isOnboarding && <OnboardingHeader />}
                        <Component {...props} />
                        <Footer />
                        <ExternalServices />
                    </>
                );

                if (permission) {
                    const permissionKey = permission[0];
                    const permissionValue = permission[1];
                    if (permissions[permissionKey]?.[permissionValue]) {
                        return mainElem;
                    }
                    // no permission takes user to home page
                    return <Redirect to={{pathname: "/"}} />;
                }

                // check search redirected from login
                const ps = new URLSearchParams(props.location.search);

                // don't allow user go anywhere if they have no listings
                // force user to add at least 1 listing
                const sources = ["login", "signup"];
                if (isJustAuthenticatedRef.current || sources.includes(ps.get("source"))) {
                    const addListingPath = "/add-listing";
                    if ((isDemo || isOnboarding) && props.location.pathname !== addListingPath) {
                        return (
                            <Redirect to={{pathname: addListingPath, search: "onboarding=true"}} />
                        );
                    }
                }

                return mainElem;
            }
            if (!isAuthenticated && !isLoading) {
                return <Redirect to={{pathname: "/login"}} />;
            }

            return <Loader center />;
        },
        [
            isAuthenticated,
            isLoading,
            isLoadingAccounts,
            query,
            user.isDemo,
            user.subscriptionStatus,
            Component,
            permission,
            permissions
        ]
    );

    return (
        <>
            <Route {...rest} render={handleRender} />
            {confirmModal && <ModalConfirm {...confirmModal} />}
        </>
    );
};

export default memo(PrivateRoute);
